// The user's CV-derived title anchors (the user_title_filter row): one query,
// one save path, one extraction trigger — shared by the 简历 page's full
// editor and the board's compact 推荐 row, so the two surfaces can never
// drift. The anchors decide what 「为你推荐」 shows on the board; AI
// pre-fills them from the CV (cv_extract_title_filter job) and the user has
// the last word chip by chip.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Chip from '../ui/Chip';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import type { TitleFilterState } from '../../lib/types';

export function useAnchors(enabled: boolean) {
  const qc = useQueryClient();
  const tfQ = useQuery({
    queryKey: ['title-filter'],
    queryFn: () => apiGet<TitleFilterState>('/api/title-filter'),
    staleTime: 30_000,
    enabled,
  });
  const anchors = tfQ.data?.domainAnchors ?? [];
  const [extractBusy, setExtractBusy] = useState(false);

  const save = (next: string[]) =>
    apiPut<TitleFilterState>('/api/title-filter', { domainAnchors: next })
      .then((data) => qc.setQueryData(['title-filter'], data))
      .catch(() => qc.invalidateQueries({ queryKey: ['title-filter'] }));

  // Fire the CV→anchors extraction (the one-off AI analysis). The job runs
  // through the queue; poll twice on the cadence SettingsFilter established.
  const runExtract = async () => {
    setExtractBusy(true);
    try {
      await apiPost('/api/title-filter/extract', {});
      setTimeout(() => qc.invalidateQueries({ queryKey: ['title-filter'] }), 30_000);
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['title-filter'] });
        setExtractBusy(false);
      }, 90_000);
    } catch {
      setExtractBusy(false);
    }
  };

  return { tfQ, anchors, save, runExtract, extractBusy };
}

/**
 * The 职位 (back) row of the Cartesian builder plus the user's CUSTOM combo
 * terms — one per-tenant store, one PUT.
 */
export function usePositions(enabled: boolean) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['board-positions'],
    queryFn: () => apiGet<{ positions: string[]; combos: string[] }>('/api/board-positions'),
    staleTime: 30_000,
    enabled,
  });
  const positions = q.data?.positions ?? [];
  const combos = q.data?.combos ?? [];
  const put = (body: { positions: string[]; combos: string[] }) =>
    apiPut<{ positions: string[]; combos: string[] }>('/api/board-positions', body)
      .then((data) => qc.setQueryData(['board-positions'], data))
      .catch(() => qc.invalidateQueries({ queryKey: ['board-positions'] }));
  const save = (next: string[]) => put({ positions: next, combos });
  const saveCombos = (next: string[]) => put({ positions, combos: next });
  return { q, positions, combos, save, saveCombos };
}

/**
 * 方向 × 职位 → combo terms, capped at 20 (the server's anchors cap). Either
 * row empty → the other row's words stand alone. The combos ARE the filter:
 * they go to /api/board as anchors=, so what the preview row shows is
 * exactly what 推荐 returns — one semantics, not two.
 */
export function crossCombos(front: string[], back: string[]): string[] {
  const f = front.map((s) => s.trim()).filter(Boolean);
  const b = back.map((s) => s.trim()).filter(Boolean);
  let out: string[];
  if (f.length && b.length) {
    out = [];
    for (const x of f) for (const y of b) out.push(`${x} ${y}`);
  } else {
    out = f.length ? [...f] : [...b];
  }
  return [...new Set(out.map((s) => s.toLowerCase()))].slice(0, 20);
}

// Role nouns that identify a term as a 职位, not a 方向. The user filed
// Engineer/Lecturer/Research Fellow under 方向 back when the rows looked
// identical — words added to 方向 whose LAST word is one of these now route
// to the 职位 row instead (conservative list; when in doubt, stay put).
const ROLE_WORDS = new Set([
  'engineer', 'engineers', 'developer', 'analyst', 'scientist', 'researcher',
  'consultant', 'lecturer', 'intern', 'manager', 'designer', 'fellow',
  'officer', 'assistant', 'associate', 'technician', 'architect',
  'administrator', 'specialist', 'coordinator',
]);
export function isRoleTerm(w: string): boolean {
  const parts = w.trim().toLowerCase().split(/\s+/);
  return ROLE_WORDS.has(parts[parts.length - 1]);
}

/** Generated combos + the user's custom ones, deduped, server cap. */
export function allCombos(front: string[], back: string[], custom: string[]): string[] {
  return [...new Set([...crossCombos(front, back), ...custom.map((s) => s.trim().toLowerCase()).filter(Boolean)])].slice(0, 20);
}

export interface GroupedCounts { anchors: Record<string, number> }

/** Per-keyword board hit counts for both axes (signed-in only; server caches). */
export function useAnchorCounts(anchors: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['board-anchor-counts', anchors.join(',')],
    queryFn: () => apiGet<{ counts: GroupedCounts }>(
      `/api/board/anchor-counts?anchors=${encodeURIComponent(anchors.join(','))}`),
    staleTime: 5 * 60_000,
    enabled: enabled && anchors.length > 0,
  });
}

/**
 * Generic chip-list editor: removable chips with live hit counts, an add
 * input. Both axes render through this so they cannot drift.
 */
export function ChipListEditor({ words, onSave, counts, placeholder, variant }: {
  words: string[];
  onSave: (next: string[]) => Promise<unknown> | void;
  counts?: Record<string, number>;
  placeholder: string;
  variant?: 'default' | 'primary' | 'second' | 'third' | 'good' | 'warn';
}) {
  const [draft, setDraft] = useState('');
  return (
    <>
      {words.map((word) => (
        <Chip key={word} variant={variant} onClick={() => void onSave(words.filter((x) => x !== word))} title="点击移除">
          {word}
          {counts?.[word.toLowerCase()] != null && ` · ${counts[word.toLowerCase()]}`}
          {' ✕'}
        </Chip>
      ))}
      <form
        style={{ display: 'inline-flex' }}
        onSubmit={(e) => {
          e.preventDefault();
          const v = draft.trim();
          if (v && !words.includes(v)) { void onSave([...words, v]); setDraft(''); }
        }}
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          style={{
            width: 96, padding: '4px 8px', borderRadius: 6, fontSize: 12,
            border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink)',
          }}
        />
      </form>
    </>
  );
}

/**
 * The Cartesian builder — the keyword module's whole UI. The STRUCTURE
 * teaches the logic (user design): editable 方向 row × editable 职位 row →
 * auto-generated 组合 row with live hit counts, plus user-added custom
 * combos. The two source rows are COLOR-CODED (方向 green, 职位 amber) with
 * described headers — they looked identical before and the user filed
 * position words under 方向 (screenshot, 2026-08-14).
 */
export function CartesianBuilder({ a, pos, hasCv }: {
  a: ReturnType<typeof useAnchors>;
  pos: ReturnType<typeof usePositions>;
  hasCv: boolean;
}) {
  const combos = allCombos(a.anchors, pos.positions, pos.combos);
  const generated = crossCombos(a.anchors, pos.positions);
  const countsQ = useAnchorCounts(combos, true);
  const counts = countsQ.data?.counts?.anchors;
  // A role word typed into 方向 lands in 职位 — the chip appears in the
  // amber row immediately, which is its own explanation.
  const saveFront = (next: string[]) => {
    const roleAdds = next.filter((w) => !a.anchors.includes(w) && isRoleTerm(w));
    if (roleAdds.length) {
      void pos.save([...new Set([...pos.positions, ...roleAdds])]);
      next = next.filter((w) => !roleAdds.includes(w));
    }
    void a.save(next);
  };
  const header = (label: string, hint: string, color: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: color, alignSelf: 'center' }} aria-hidden="true" />
      <span style={{ fontSize: 13, fontWeight: 650 }}>{label}</span>
      <span className="page-sub" style={{ marginTop: 0, fontSize: 12 }}>{hint}</span>
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div>
        {header('方向', '你的领域，如 security、ML、data', 'var(--pri)')}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {a.anchors.length === 0 && !a.extractBusy && hasCv && (
            <button className="btn" style={{ fontSize: 12.5 }} onClick={a.runExtract}>
              ⭐ 从我的简历提取
            </button>
          )}
          {a.extractBusy && a.anchors.length === 0 && (
            <span className="page-sub" style={{ marginTop: 0 }}>正在分析你的简历，大约一分钟…</span>
          )}
          <ChipListEditor words={a.anchors} onSave={saveFront} placeholder="＋如 security" variant="primary" />
          {a.anchors.length > 0 && (
            <button
              className="btn ghost"
              style={{ fontSize: 12 }}
              disabled={a.extractBusy}
              onClick={a.runExtract}
              title="用 AI 重新从你的简历提取方向词（会覆盖当前列表）"
            >
              {a.extractBusy ? '分析中…' : '重新分析'}
            </button>
          )}
        </div>
      </div>
      <div style={{ fontSize: 16, color: 'var(--ink3)', padding: '2px 0 2px 1px' }} aria-hidden="true">×</div>
      <div>
        {header('职位', '你想投的岗位类型，如 engineer、lecturer、intern', '#b8860b')}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <ChipListEditor words={pos.positions} onSave={pos.save} placeholder="＋如 engineer" variant="second" />
        </div>
      </div>
      <div className="hair" style={{ margin: '10px 0 6px' }} />
      <div>
        {header('组合', '方向 × 职位 自动生成 · 也可以直接添加自己的组合 · 数字 = 当前命中职位数', 'var(--ink3)')}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {generated.map((c) => (
            <Chip key={c} title="自动生成：由上面两行相乘得到，删上面的词它会消失">
              {c}
              {counts?.[c] != null && ` · ${counts[c]}`}
            </Chip>
          ))}
          <ChipListEditor
            words={pos.combos}
            onSave={pos.saveCombos}
            counts={counts}
            placeholder="＋自定义组合"
          />
          {combos.length === 0 && (
            <span className="page-sub" style={{ marginTop: 0 }}>上面加词后，这里自动生成组合。</span>
          )}
        </div>
      </div>
    </div>
  );
}
